/**
 * API Service Layer
 * Comprehensive API service with UUID support
 */

import api from '../utils/api';

// ============================================
// AUTH SERVICE
// ============================================
export const authService = {
  login: async (username, password) => {
    const response = await api.post('/api/auth/login', { username, password });
    return response.data;
  },

  logout: async () => {
    const response = await api.post('/api/auth/logout');
    return response.data;
  },

  refresh: async (refreshToken) => {
    const response = await api.post('/api/auth/refresh', { refresh_token: refreshToken });
    return response.data;
  },

  getCurrentUser: async () => {
    const response = await api.get('/api/me');
    return response.data;
  }
};

// ============================================
// USER SERVICE
// ============================================
export const userService = {
  // Get all users
  getAllUsers: async () => {
    const response = await api.get('/api/users');
    return response.data;
  },

  // Get user by ID or UUID
  getUser: async (identifier) => {
    const response = await api.get(`/api/users/${identifier}`);
    return response.data;
  },

  // Create new user
  createUser: async (userData) => {
    const response = await api.post('/api/users/create', userData);
    return response.data;
  },

  // Update user
  updateUser: async (identifier, userData) => {
    const response = await api.put(`/api/users/${identifier}`, userData);
    return response.data;
  },

  // Delete user
  deleteUser: async (identifier) => {
    const response = await api.delete(`/api/users/${identifier}`);
    return response.data;
  },

  // Get renters only (with enriched contract/room data)
  getRenters: async () => {
    const response = await api.get('/api/renters');
    return response.data;
  },

  // Get renter details
  getRenterDetails: async (identifier) => {
    const response = await api.get(`/api/renters/${identifier}`);
    return response.data;
  },

  // Get current user (alias for authService.getCurrentUser)
  getCurrentUser: async () => {
    const response = await api.get('/api/me');
    return response.data;
  }
};

// ============================================
// PROPERTY SERVICE
// ============================================
export const propertyService = {
  // Buildings
  getBuildings: async () => {
    const response = await api.get('/api/properties/buildings');
    return response.data;
  },

  getBuilding: async (identifier) => {
    const response = await api.get(`/api/properties/buildings/${identifier}`);
    return response.data;
  },

  createBuilding: async (buildingData) => {
    const response = await api.post('/api/properties/buildings', buildingData);
    return response.data;
  },

  updateBuilding: async (identifier, buildingData) => {
    const response = await api.put(`/api/properties/buildings/${identifier}`, buildingData);
    return response.data;
  },

  deleteBuilding: async (identifier) => {
    const response = await api.delete(`/api/properties/buildings/${identifier}`);
    return response.data;
  },

  // Rooms
  getRooms: async () => {
    const response = await api.get('/api/properties/rooms');
    return response.data;
  },

  getRoom: async (identifier) => {
    const response = await api.get(`/api/properties/rooms/${identifier}`);
    return response.data;
  },

  getRoomsByBuilding: async (buildingId) => {
    const response = await api.get(`/api/properties/buildings/${buildingId}/rooms`);
    return response.data;
  },

  // Alias for getRoomsByBuilding (used by PropertiesEnhanced.jsx)
  getBuildingRooms: async (buildingId) => {
    const response = await api.get(`/api/properties/buildings/${buildingId}/rooms`);
    return response.data;
  },

  getRoomDetails: async (identifier) => {
    const response = await api.get(`/api/properties/rooms/${identifier}/details`);
    return response.data;
  },

  getRoomHistory: async (identifier) => {
    const response = await api.get(`/api/properties/rooms/${identifier}/history`);
    return response.data;
  },

  getRoomFinancial: async (identifier) => {
    const response = await api.get(`/api/properties/rooms/${identifier}/financial`);
    return response.data;
  },

  createRoom: async (roomData) => {
    const response = await api.post('/api/properties/rooms', roomData);
    return response.data;
  },

  updateRoom: async (identifier, roomData) => {
    const response = await api.put(`/api/properties/rooms/${identifier}`, roomData);
    return response.data;
  },

  deleteRoom: async (identifier) => {
    const response = await api.delete(`/api/properties/rooms/${identifier}`);
    return response.data;
  },

  // Bulk update room pricing for a building
  bulkUpdateRoomPricing: async (buildingId, pricingData) => {
    // pricingData: { monthly_rent, apply_to_status?: string[], floor?: number }
    const response = await api.put(`/api/properties/buildings/${buildingId}/rooms/bulk-pricing`, pricingData);
    return response.data;
  },

  assignRoom: async (roomId, rentersData) => {
    const response = await api.post(`/api/rooms/${roomId}/assign`, rentersData);
    return response.data;
  },

  // Furniture
  getRoomFurniture: async (roomId) => {
    const response = await api.get(`/api/properties/rooms/${roomId}/furniture`);
    return response.data;
  },

  addFurniture: async (roomId, furnitureData) => {
    const response = await api.post(`/api/properties/rooms/${roomId}/furniture`, furnitureData);
    return response.data;
  },

  updateFurniture: async (identifier, furnitureData) => {
    const response = await api.put(`/api/properties/furniture/${identifier}`, furnitureData);
    return response.data;
  },

  deleteFurniture: async (identifier) => {
    const response = await api.delete(`/api/properties/furniture/${identifier}`);
    return response.data;
  },

  // Analytics
  getPropertyStats: async () => {
    const response = await api.get('/api/properties/stats');
    return response.data;
  },

  getOccupancyTrend: async () => {
    const response = await api.get('/api/properties/analytics/occupancy-trend');
    return response.data;
  },

  getRevenueAnalytics: async () => {
    const response = await api.get('/api/properties/analytics/revenue');
    return response.data;
  },

  // Furniture Templates
  getFurnitureTemplates: async (filters = {}) => {
    const params = new URLSearchParams(filters).toString();
    const response = await api.get(`/api/furniture-templates${params ? '?' + params : ''}`);
    return response.data;
  },

  getFurnitureTemplate: async (templateId) => {
    const response = await api.get(`/api/furniture-templates/${templateId}`);
    return response.data;
  },

  createFurnitureTemplate: async (templateData) => {
    const response = await api.post('/api/furniture-templates', templateData);
    return response.data;
  },

  updateFurnitureTemplate: async (templateId, templateData) => {
    const response = await api.put(`/api/furniture-templates/${templateId}`, templateData);
    return response.data;
  },

  deleteFurnitureTemplate: async (templateId) => {
    const response = await api.delete(`/api/furniture-templates/${templateId}`);
    return response.data;
  },

  applyFurnitureTemplate: async (templateId, roomId) => {
    const response = await api.post('/api/furniture-templates/apply', {
      template_id: templateId,
      room_id: roomId
    });
    return response.data;
  },

  // Furniture Catalog (per-building)
  getFurnitureCatalog: async (buildingId, filters = {}) => {
    const params = new URLSearchParams(filters).toString();
    const response = await api.get(`/api/properties/buildings/${buildingId}/furniture-catalog${params ? '?' + params : ''}`);
    return response.data;
  },

  getCatalogItem: async (catalogId) => {
    const response = await api.get(`/api/properties/furniture-catalog/${catalogId}`);
    return response.data;
  },

  createCatalogItem: async (buildingId, catalogData) => {
    const response = await api.post(`/api/properties/buildings/${buildingId}/furniture-catalog`, catalogData);
    return response.data;
  },

  updateCatalogItem: async (catalogId, catalogData) => {
    const response = await api.put(`/api/properties/furniture-catalog/${catalogId}`, catalogData);
    return response.data;
  },

  deleteCatalogItem: async (catalogId) => {
    const response = await api.delete(`/api/properties/furniture-catalog/${catalogId}`);
    return response.data;
  },

  // Templates by building
  getTemplatesByBuilding: async (buildingId) => {
    const response = await api.get(`/api/furniture-templates?building_id=${buildingId}`);
    return response.data;
  }
};

// ============================================
// CONTRACT SERVICE
// ============================================
export const contractService = {
  // Contracts
  getContracts: async (filters = {}) => {
    const params = new URLSearchParams(filters).toString();
    const response = await api.get(`/api/contracts${params ? '?' + params : ''}`);
    return response.data;
  },

  getContract: async (identifier) => {
    const response = await api.get(`/api/contracts/${identifier}`);
    return response.data;
  },

  generateContract: async (contractData) => {
    const response = await api.post('/api/contracts/generate', contractData);
    return response.data;
  },

  updateContract: async (identifier, contractData) => {
    const response = await api.put(`/api/contracts/${identifier}`, contractData);
    return response.data;
  },

  terminateContract: async (identifier, reason) => {
    const response = await api.post(`/api/contracts/${identifier}/terminate`, { reason });
    return response.data;
  },

  signContract: async (contractId, signerId) => {
    const response = await api.put(`/api/contracts/${contractId}/sign`, { signer_id: signerId });
    return response.data;
  },

  renewContract: async (contractId, renewalData) => {
    const response = await api.post(`/api/contracts/${contractId}/renew`, renewalData);
    return response.data;
  },

  reactivateContract: async (contractId) => {
    const response = await api.put(`/api/contracts/${contractId}/reactivate`);
    return response.data;
  },

  deleteContract: async (contractId) => {
    const response = await api.delete(`/api/contracts/${contractId}`);
    return response.data;
  },

  downloadContract: async (contractId) => {
    const response = await api.get(`/api/contracts/${contractId}/download`, {
      responseType: 'blob'
    });
    return response.data;
  },

  // Export contract document as Word (docx) or PDF
  exportContractDocument: async (contractId, documentType = 'contract', format = 'docx') => {
    const response = await api.get(`/api/contracts/${contractId}/export`, {
      params: { format, document_type: documentType },
      responseType: 'blob'
    });
    return response.data;
  },

  // Export contract as PDF (convenience method)
  exportContractPdf: async (contractId, documentType = 'contract') => {
    const response = await api.get(`/api/contracts/${contractId}/export`, {
      params: { format: 'pdf', document_type: documentType },
      responseType: 'blob'
    });
    return response.data;
  },

  // Export contract using uploaded template
  exportFromTemplate: async (contractId, templateId, format = 'docx') => {
    const response = await api.get(`/api/contracts/${contractId}/export-from-template/${templateId}`, {
      params: { format },
      responseType: 'blob'
    });
    return response.data;
  },

  // Get contract export data (for preview)
  getContractExportData: async (contractId) => {
    const response = await api.get(`/api/contracts/${contractId}/export-data`);
    return response.data;
  },

  // Preview template with sample or actual data
  previewTemplate: async (templateId, contractId = null) => {
    const params = contractId ? { contract_id: contractId } : {};
    const response = await api.post(`/api/contracts/templates/${templateId}/preview`, null, { params });
    return response.data;
  },

  // Get available placeholders for templates
  getAvailablePlaceholders: async () => {
    const response = await api.get('/api/contracts/templates/placeholders');
    return response.data;
  },

  // Contract tenants management
  addTenantToContract: async (contractId, tenantId) => {
    const response = await api.post(`/api/contracts/${contractId}/tenants`, { tenant_id: tenantId });
    return response.data;
  },

  // Alias for addTenantToContract (used by ContractsEnhanced.jsx)
  addTenant: async (contractId, tenantId, isPrimary = false) => {
    const response = await api.post(`/api/contracts/${contractId}/tenants`, {
      tenant_id: tenantId,
      is_primary: isPrimary
    });
    return response.data;
  },

  removeTenantFromContract: async (contractId, tenantId) => {
    const response = await api.delete(`/api/contracts/${contractId}/tenants/${tenantId}`);
    return response.data;
  },

  // Alias for removeTenantFromContract (used by ContractsEnhanced.jsx)
  removeTenant: async (contractId, tenantId) => {
    const response = await api.delete(`/api/contracts/${contractId}/tenants/${tenantId}`);
    return response.data;
  },

  setPrimaryTenant: async (contractId, tenantId) => {
    const response = await api.put(`/api/contracts/${contractId}/tenants/${tenantId}/primary`);
    return response.data;
  },

  // Templates
  getTemplates: async () => {
    const response = await api.get('/api/contracts/templates');
    return response.data;
  },

  getTemplate: async (identifier) => {
    const response = await api.get(`/api/contracts/templates/${identifier}`);
    return response.data;
  },

  createTemplate: async (templateData) => {
    const response = await api.post('/api/contracts/templates', templateData);
    return response.data;
  },

  updateTemplate: async (identifier, templateData) => {
    const response = await api.put(`/api/contracts/templates/${identifier}`, templateData);
    return response.data;
  },

  deleteTemplate: async (identifier) => {
    const response = await api.delete(`/api/contracts/templates/${identifier}`);
    return response.data;
  },

  // Upload Word template file (.doc/.docx)
  uploadTemplate: async (file, templateName, description = '') => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('name', templateName);
    if (description) {
      formData.append('description', description);
    }
    const response = await api.post('/api/contracts/templates/upload', formData, {
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    });
    return response.data;
  },

  // Download template file
  downloadTemplate: async (templateId) => {
    const response = await api.get(`/api/contracts/templates/${templateId}/download`, {
      responseType: 'blob'
    });
    return response.data;
  },

  // Get placeholders from uploaded template
  getTemplatePlaceholders: async (templateId) => {
    const response = await api.get(`/api/contracts/templates/${templateId}/placeholders`);
    return response.data;
  }
};

// ============================================
// PAYMENT/FINANCE SERVICE
// ============================================
export const financeService = {
  // Payments
  getPayments: async (filters = {}) => {
    const params = new URLSearchParams(filters).toString();
    const response = await api.get(`/api/admin/payments${params ? '?' + params : ''}`);
    return response.data;
  },

  getExpenses: async (filters = {}) => {
    const params = new URLSearchParams(filters).toString();
    const response = await api.get(`/api/expenses${params ? '?' + params : ''}`);
    return Array.isArray(response.data) ? response.data : [];
  },

  getPayment: async (identifier) => {
    const response = await api.get(`/api/payments/${identifier}`);
    return response.data;
  },

  getPaymentHistory: async (identifier) => {
    const response = await api.get(`/api/payments/${identifier}/history`);
    return response.data;
  },

  confirmPayment: async (identifier, confirmationData) => {
    const response = await api.put(`/api/payments/${identifier}/confirm`, confirmationData);
    return response.data;
  },

  rejectPayment: async (identifier, reason) => {
    const response = await api.put(`/api/payments/${identifier}/reject`, { reason });
    return response.data;
  },

  uploadPaymentProof: async (paymentId, formData) => {
    const response = await api.post(`/api/payments/${paymentId}/proof`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
    return response.data;
  },

  createPayment: async (paymentData) => {
    const response = await api.post('/api/payments', paymentData);
    return response.data;
  },

  verifyPayment: async (paymentId, verificationData) => {
    const response = await api.put(`/api/tickets/payment/${paymentId}/verify`, verificationData);
    return response.data;
  },

  // Invoices
  getInvoices: async () => {
    const response = await api.get('/api/invoices');
    return response.data;
  },

  createInvoice: async (invoiceData) => {
    const response = await api.post('/api/invoices', invoiceData);
    return response.data;
  },

  // Financial stats
  getFinancialStats: async () => {
    const response = await api.get('/api/finance/stats');
    return response.data;
  },

  getRevenueReport: async (period) => {
    const response = await api.get(`/api/finance/revenue-report?period=${period}`);
    return response.data;
  },

  // Revenue Analytics (based on actual payments)
  getRevenueAnalytics: async (params = {}) => {
    const queryParams = new URLSearchParams();
    if (params.year) queryParams.append('year', params.year);
    if (params.month) queryParams.append('month', params.month);
    if (params.buildingId) queryParams.append('building_id', params.buildingId);
    if (params.startMonth) queryParams.append('start_month', params.startMonth);
    if (params.endMonth) queryParams.append('end_month', params.endMonth);
    const queryString = queryParams.toString();
    const response = await api.get(`/api/analytics/revenue${queryString ? '?' + queryString : ''}`);
    return response.data;
  },

  // Payment Transactions
  getPaymentTransactions: async (paymentId) => {
    const response = await api.get(`/api/payments/${paymentId}/transactions`);
    return response.data;
  },

  getPaymentConversation: async (paymentId) => {
    const response = await api.get(`/api/payments/${paymentId}/conversation`);
    return response.data;
  },

  reviewTransaction: async (transactionId, reviewData) => {
    const response = await api.put(`/api/admin/transactions/${transactionId}/review`, reviewData);
    return response.data;
  },

  editTransaction: async (transactionId, editData) => {
    const response = await api.put(`/api/admin/transactions/${transactionId}/edit`, editData);
    return response.data;
  },

  deleteTransaction: async (transactionId) => {
    const response = await api.delete(`/api/transactions/${transactionId}`);
    return response.data;
  },

  // Meter Readings
  getMeterReadings: async (filters = {}) => {
    const params = new URLSearchParams(filters).toString();
    const response = await api.get(`/api/meter-readings${params ? '?' + params : ''}`);
    return response.data;
  },

  getMeterReading: async (identifier) => {
    const response = await api.get(`/api/meter-readings/${identifier}`);
    return response.data;
  },

  createMeterReading: async (readingData) => {
    const response = await api.post('/api/meter-readings', readingData);
    return response.data;
  },

  updateMeterReading: async (identifier, readingData) => {
    const response = await api.put(`/api/meter-readings/${identifier}`, readingData);
    return response.data;
  },

  verifyMeterReading: async (identifier, verificationData) => {
    const response = await api.put(`/api/meter-readings/${identifier}/verify`, verificationData);
    return response.data;
  },

  // Meter Usage Analytics
  getMeterUsageByRooms: async (readingType, params = {}) => {
    const queryParams = new URLSearchParams();
    queryParams.append('reading_type', readingType);
    if (params.startMonth) queryParams.append('start_month', params.startMonth);
    if (params.endMonth) queryParams.append('end_month', params.endMonth);
    if (params.buildingId) queryParams.append('building_id', params.buildingId);
    const response = await api.get(`/api/analytics/meter-usage/rooms?${queryParams.toString()}`);
    return response.data;
  },

  getBuildingMeterComparison: async (readingType, params = {}) => {
    const queryParams = new URLSearchParams();
    queryParams.append('reading_type', readingType);
    if (params.month) queryParams.append('month', params.month);
    const response = await api.get(`/api/analytics/meter-usage/building-comparison?${queryParams.toString()}`);
    return response.data;
  },

  getMeterPrediction: async (params = {}) => {
    const queryParams = new URLSearchParams();
    if (params.monthsAhead) queryParams.append('months_ahead', params.monthsAhead);
    if (params.buildingId) queryParams.append('building_id', params.buildingId);
    const queryString = queryParams.toString();
    const response = await api.get(`/api/analytics/meter-prediction${queryString ? '?' + queryString : ''}`);
    return response.data;
  },

  // Payment Adjustments (Service Fee Waivers/Credits)
  getAdjustments: async (filters = {}) => {
    const params = new URLSearchParams();
    if (filters.paymentId) params.append('payment_id', filters.paymentId);
    if (filters.buildingId) params.append('building_id', filters.buildingId);
    if (filters.month) params.append('month', filters.month);
    if (filters.status) params.append('status', filters.status);
    if (filters.limit) params.append('limit', filters.limit);
    if (filters.offset) params.append('offset', filters.offset);
    const queryString = params.toString();
    const response = await api.get(`/api/adjustments${queryString ? '?' + queryString : ''}`);
    return response.data;
  },

  getAdjustment: async (adjustmentId) => {
    const response = await api.get(`/api/adjustments/${adjustmentId}`);
    return response.data;
  },

  getPaymentAdjustments: async (paymentId) => {
    const response = await api.get(`/api/payments/${paymentId}/adjustments`);
    return response.data;
  },

  createAdjustment: async (adjustmentData) => {
    // adjustmentData can be:
    // Single: { payment_id, service_id, service_name, reason, adjustment_percent }
    // Building-wide: { building_id, month, service_id, service_name, reason, adjustment_percent }
    const response = await api.post('/api/adjustments', adjustmentData);
    return response.data;
  },

  reverseAdjustment: async (adjustmentId) => {
    const response = await api.delete(`/api/adjustments/${adjustmentId}`);
    return response.data;
  },

  // Adjustments Analytics for Dashboard
  getAdjustmentsAnalytics: async (filters = {}) => {
    const params = new URLSearchParams();
    if (filters.startMonth) params.append('start_month', filters.startMonth);
    if (filters.endMonth) params.append('end_month', filters.endMonth);
    if (filters.buildingId) params.append('building_id', filters.buildingId);
    const queryString = params.toString();
    const response = await api.get(`/api/analytics/adjustments${queryString ? '?' + queryString : ''}`);
    return response.data;
  }
};

// ============================================
// TICKET SERVICE
// ============================================
export const ticketService = {
  // Admin tickets
  getTickets: async (filters = {}) => {
    const params = new URLSearchParams(filters).toString();
    const response = await api.get(`/api/tickets${params ? '?' + params : ''}`);
    return response.data;
  },

  getTicket: async (identifier) => {
    const response = await api.get(`/api/tickets/${identifier}`);
    return response.data;
  },

  createTicket: async (ticketData) => {
    const response = await api.post('/api/tickets', ticketData);
    return response.data;
  },

  updateTicket: async (identifier, ticketData) => {
    const response = await api.put(`/api/tickets/${identifier}`, ticketData);
    return response.data;
  },

  // Renter tickets (isolated endpoints)
  getRenterTickets: async (filters = {}) => {
    const params = new URLSearchParams(filters).toString();
    const response = await api.get(`/api/renters/tickets${params ? '?' + params : ''}`);
    return response.data;
  },

  createRenterTicket: async (ticketData) => {
    const response = await api.post('/api/renters/tickets', ticketData);
    return response.data;
  },

  getRenterTicketDetail: async (ticketId) => {
    const response = await api.get(`/api/renters/tickets/${ticketId}`);
    return response.data;
  },

  getRenterTicketComments: async (ticketId) => {
    const response = await api.get(`/api/renters/tickets/${ticketId}/comments`);
    return response.data;
  },

  addRenterTicketComment: async (ticketId, comment) => {
    const response = await api.post(`/api/renters/tickets/${ticketId}/comments`, {
      comment: comment
    });
    return response.data;
  },

  getRenterTicketPayments: async (ticketId) => {
    const response = await api.get(`/api/renters/tickets/${ticketId}/payments`);
    return response.data;
  },

  submitRenterTicketPayment: async (ticketId, paymentData) => {
    const response = await api.post(`/api/renters/tickets/${ticketId}/payment`, paymentData);
    return response.data;
  },

  // Comments
  addComment: async (ticketId, comment) => {
    const response = await api.post(`/api/tickets/${ticketId}/comments`, {
      ticket_id: ticketId,
      comment: comment
    });
    return response.data;
  },

  getComments: async (ticketId) => {
    const response = await api.get(`/api/tickets/${ticketId}/comments`);
    return response.data;
  },

  // Additional ticket methods
  getTicketDetail: async (ticketId) => {
    const response = await api.get(`/api/tickets/${ticketId}`);
    return response.data;
  },

  getTicketPayments: async (ticketId) => {
    const response = await api.get(`/api/tickets/${ticketId}/payments`);
    return response.data;
  },

  updateTicketStatus: async (ticketId, status) => {
    const response = await api.put(`/api/tickets/${ticketId}/status`, { status });
    return response.data;
  },

  updateServiceCharge: async (ticketId, charge) => {
    const response = await api.put(`/api/tickets/${ticketId}/service-charge`, {
      service_charge: charge
    });
    return response.data;
  },

  // Get tickets for a specific furniture item
  getTicketsByFurniture: async (furnitureUuid) => {
    const response = await api.get(`/api/tickets/by-furniture/${furnitureUuid}`);
    return response.data;
  }
};

// ============================================
// VIOLATION SERVICE (NEW)
// ============================================
export const violationService = {
  // Violations
  getViolations: async (filters = {}) => {
    // Filter out undefined/null values to prevent "undefined" being sent as a string
    const cleanFilters = Object.fromEntries(
      Object.entries(filters).filter(([_, v]) => v !== undefined && v !== null && v !== '')
    );
    const params = new URLSearchParams(cleanFilters).toString();
    const response = await api.get(`/api/violations${params ? '?' + params : ''}`);
    return response.data;
  },

  getViolation: async (violationUuid) => {
    const response = await api.get(`/api/violations/${violationUuid}`);
    return response.data;
  },

  createViolation: async (violationData) => {
    const response = await api.post('/api/violations', violationData);
    return response.data;
  },

  updateViolation: async (violationUuid, updateData) => {
    const response = await api.patch(`/api/violations/${violationUuid}`, updateData);
    return response.data;
  },

  // Evidence
  uploadEvidence: async (violationUuid, formData) => {
    const response = await api.post(`/api/violations/${violationUuid}/evidence`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
    return response.data;
  },

  getEvidence: async (violationUuid, evidenceId) => {
    const response = await api.get(`/api/violations/${violationUuid}/evidence/${evidenceId}`, {
      responseType: 'blob'
    });
    return response.data;
  },

  deleteEvidence: async (violationUuid, evidenceId) => {
    const response = await api.delete(`/api/violations/${violationUuid}/evidence/${evidenceId}`);
    return response.data;
  },

  // Comments
  addViolationComment: async (violationUuid, commentData) => {
    const response = await api.post(`/api/violations/${violationUuid}/comments`, commentData);
    return response.data;
  },

  getViolationComments: async (violationUuid, includeInternal = false) => {
    const response = await api.get(`/api/violations/${violationUuid}/comments?include_internal=${includeInternal}`);
    return response.data;
  }
};

// ============================================
// NOTIFICATION SERVICE (NEW)
// ============================================
export const notificationService = {
  // Notifications
  sendNotification: async (notificationData) => {
    const response = await api.post('/api/notifications', notificationData);
    return response.data;
  },

  broadcastNotification: async (broadcastData) => {
    const response = await api.post('/api/notifications/broadcast', broadcastData);
    return response.data;
  },

  getUserNotifications: async (userId, filters = {}) => {
    const params = new URLSearchParams(filters).toString();
    const response = await api.get(`/api/notifications/user/${userId}${params ? '?' + params : ''}`);
    return response.data;
  },

  markAsRead: async (notificationUuid) => {
    const response = await api.patch(`/api/notifications/${notificationUuid}/read`);
    return response.data;
  },

  getUnreadCount: async (userId) => {
    const response = await api.get(`/api/notifications/unread-count/${userId}`);
    return response.data;
  },

  // Preferences
  getNotificationPreferences: async (userId) => {
    const response = await api.get(`/api/notifications/preferences/${userId}`);
    return response.data;
  },

  updateNotificationPreferences: async (userId, preferences) => {
    const response = await api.put(`/api/notifications/preferences/${userId}`, preferences);
    return response.data;
  },

  // WebSocket connection helper
  connectWebSocket: (userId, onMessage) => {
    const wsProtocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
    const wsUrl = `${wsProtocol}//${window.location.host}/ws/${userId}`;

    const ws = new WebSocket(wsUrl);

    ws.onopen = () => {
      console.log('WebSocket connected');
    };

    ws.onmessage = (event) => {
      const data = JSON.parse(event.data);
      onMessage(data);
    };

    ws.onerror = (error) => {
      console.error('WebSocket error:', error);
    };

    ws.onclose = () => {
      console.log('WebSocket disconnected');
      // Auto-reconnect after 3 seconds
      setTimeout(() => notificationService.connectWebSocket(userId, onMessage), 3000);
    };

    // Ping interval to keep connection alive
    const pingInterval = setInterval(() => {
      if (ws.readyState === WebSocket.OPEN) {
        ws.send('ping');
      }
    }, 30000);

    // Return cleanup function
    return () => {
      clearInterval(pingInterval);
      ws.close();
    };
  }
};

// ============================================
// DASHBOARD SERVICE
// ============================================
export const dashboardService = {
  getWidgets: async () => {
    const response = await api.get('/api/dashboard/widgets');
    return response.data;
  },

  saveWidgets: async (widgets) => {
    const response = await api.post('/api/dashboard/widgets', { widgets });
    return response.data;
  },

  getDashboardStats: async () => {
    const response = await api.get('/api/stats/dashboard');
    return response.data;
  }
};

// ============================================
// VEHICLE SERVICE
// ============================================
export const vehicleService = {
  getVehicles: async () => {
    const response = await api.get('/api/vehicles');
    return response.data;
  },

  registerVehicle: async (vehicleData) => {
    const response = await api.post('/api/vehicles', vehicleData);
    return response.data;
  },

  updateVehicle: async (identifier, vehicleData) => {
    const response = await api.put(`/api/vehicles/${identifier}`, vehicleData);
    return response.data;
  },

  deleteVehicle: async (identifier) => {
    const response = await api.delete(`/api/vehicles/${identifier}`);
    return response.data;
  },

  getAccessLog: async (filters = {}) => {
    const params = new URLSearchParams(filters).toString();
    const response = await api.get(`/api/vehicles/access-log${params ? '?' + params : ''}`);
    return response.data;
  }
};

// ============================================
// RENTER PORTAL SERVICE
// ============================================
export const renterPortalService = {
  getDashboard: async () => {
    const response = await api.get('/api/renters/dashboard');
    return response.data;
  },

  getMyContract: async () => {
    const response = await api.get('/api/renters/contract');
    return response.data;
  },

  getMyPayments: async () => {
    const response = await api.get('/api/renters/payments');
    return response.data;
  },

  uploadMeterReading: async (formData) => {
    const response = await api.post('/api/renters/meter-reading', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
    return response.data;
  }
};

// ============================================
// ERROR HANDLING WRAPPER
// ============================================
export const withErrorHandling = async (apiCall, errorCallback) => {
  try {
    const result = await apiCall();
    return { success: true, data: result };
  } catch (error) {
    console.error('API Error:', error);

    // Extract error message
    const errorMessage = error.response?.data?.detail ||
                        error.response?.data?.message ||
                        error.message ||
                        'An unexpected error occurred';

    // Call error callback if provided
    if (errorCallback) {
      errorCallback(errorMessage);
    }

    return {
      success: false,
      error: errorMessage,
      statusCode: error.response?.status
    };
  }
};

// Export all services
export default {
  auth: authService,
  user: userService,
  property: propertyService,
  contract: contractService,
  finance: financeService,
  ticket: ticketService,
  violation: violationService,
  notification: notificationService,
  dashboard: dashboardService,
  vehicle: vehicleService,
  renterPortal: renterPortalService,
  withErrorHandling
};